import { useState } from 'react';
import {
  Thermometer,
  AlertTriangle,
  Sliders,
  Eye,
  Calculator,
  Wrench,
  ArrowRight,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

interface ThermalPreset {
  id: string;
  name: string;
  assetTag: string;
  imageUrl: string;
  ambientTemp: number;
  peakTemp: number;
  deltaT: number;
  wettedAreaPercent: number;
  moistureProb: number;
  severity: 'critical' | 'high' | 'medium' | 'low';
  description: string;
  recommendation: string;
}

const THERMAL_PRESETS: ThermalPreset[] = [
  {
    id: 'preset-1',
    name: 'Sample 1: Radiometric FLIR Hotspot (12"-HC-2019)',
    assetTag: '12"-HC-2019-CS',
    imageUrl: '/thermal_flir.jpeg',
    ambientTemp: 26.1,
    peakTemp: 37.4,
    deltaT: 11.3,
    wettedAreaPercent: 32,
    moistureProb: 0.96,
    severity: 'critical',
    description: 'FLIR radiometric thermogram reveals prominent wetted insulation pocket (Sp1: 37.4°C vs pipe baseline 26.1°C, Emissivity 0.96). Significant heat loss and saturated calcium silicate matrix detected.',
    recommendation: 'Targeted stripping of 1.2m section. Immediate high-priority dispatch to SAP PM for localized patch & drying.',
  },
  {
    id: 'preset-2',
    name: 'Sample 2: Flange Ring Water Trapping (COLUMN-C101)',
    assetTag: 'COLUMN-C101-FEED',
    imageUrl: '/banner.png',
    ambientTemp: 21.0,
    peakTemp: 26.8,
    deltaT: 5.8,
    wettedAreaPercent: 14,
    moistureProb: 0.82,
    severity: 'high',
    description: 'Evaporative cooling gradient adjacent to unsealed flange perimeter indicates rainwater ingress behind weather-wrap.',
    recommendation: 'Remove flange cover, dry insulation, install new high-temp silicone mastic weather-jacket seal.',
  },
  {
    id: 'preset-3',
    name: 'Sample 3: Sound Dry Insulation Baseline (V-102)',
    assetTag: 'V-102-AMINE-TOWER',
    imageUrl: '/banner.png',
    ambientTemp: 23.5,
    peakTemp: 24.1,
    deltaT: 0.6,
    wettedAreaPercent: 0,
    moistureProb: 0.04,
    severity: 'low',
    description: 'Uniform surface temperature gradient conforming to expected conductive heat transfer. No moisture accumulation detected.',
    recommendation: 'Insulation barrier certified intact. Re-certify on standard 36-month RBI frequency.',
  },
];

export function ThermalAnalysisPage() {
  const [selectedPreset, setSelectedPreset] = useState<ThermalPreset>(THERMAL_PRESETS[0]);
  const [isLoading, setIsLoading] = useState(false);
  const [isothermThreshold, setIsothermThreshold] = useState<number>(5.0);
  const [showIsotherms, setShowIsotherms] = useState<boolean>(true);

  const handleSelectPreset = (preset: ThermalPreset) => {
    setSelectedPreset(preset);
    toast.success(`Loaded ${preset.name}`);
  };

  const handleAnalyze = async () => {
    setIsLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 800));
      toast.success('Radiometric thermal analysis updated successfully');
    } catch {
      toast.error('Failed to analyze thermal frame');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="telemetry-pill bg-amber-50 text-amber-800 font-bold border border-amber-200">
                RADIOMETRIC THERMOGRAPHY // NETD &lt; 30mK
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight flex items-center gap-2.5">
              <Thermometer className="h-7 w-7 text-amber-600" />
              Thermal Moisture Analysis
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Non-destructive identification of wetted insulation pockets using infrared thermography and solar-normalized isothermal differentials.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Link to="/tools/ffs" className="btn btn-secondary text-xs sm:text-sm py-2 px-3 shadow-sm">
              <Calculator className="h-4 w-4 text-blue-600" />
              <span>Evaluate FFS</span>
            </Link>
            <Link to="/cmms" className="btn btn-primary text-xs sm:text-sm py-2 px-3 shadow-sm">
              <Wrench className="h-4 w-4" />
              <span>Dispatch CMMS</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Preset Selector Bar */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-xs font-mono font-bold text-slate-700">
          <Sliders className="h-4 w-4 text-amber-600" />
          <span>SELECT RADIOMETRIC DATASET:</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {THERMAL_PRESETS.map((p) => (
            <button
              key={p.id}
              onClick={() => handleSelectPreset(p)}
              className={`btn text-xs py-1.5 px-3 rounded-lg font-medium transition-all ${
                selectedPreset.id === p.id
                  ? 'btn-primary shadow-sm'
                  : 'btn-secondary text-slate-700 hover:bg-white'
              }`}
            >
              {p.name}
            </button>
          ))}
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Left Column: False-Color Radiometric Preview */}
        <div className="lg:col-span-7 space-y-4">
          <div className="card overflow-hidden bg-slate-950 border border-slate-800 shadow-xl rounded-xl">
            {/* Toolbar */}
            <div className="p-3 bg-slate-900 border-b border-slate-800 flex items-center justify-between text-xs font-mono text-slate-300">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-amber-400 animate-pulse" />
                <span>FLIR SC-650 // ASSET: {selectedPreset.assetTag}</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowIsotherms(!showIsotherms)}
                  className={`px-2 py-1 rounded text-[11px] font-mono border transition-colors ${
                    showIsotherms
                      ? 'bg-amber-600 text-white border-amber-500'
                      : 'bg-slate-800 text-slate-400 border-slate-700'
                  }`}
                >
                  <Eye className="h-3 w-3 inline mr-1" />
                  {showIsotherms ? 'Isotherms: ON' : 'Isotherms: OFF'}
                </button>
              </div>
            </div>

            {/* Thermal Display Canvas */}
            <div className="relative min-h-[380px] max-h-[480px] flex items-center justify-center bg-slate-950 overflow-hidden">
              <img
                src={selectedPreset.imageUrl}
                alt="Radiometric Thermal Frame"
                className="w-full h-full object-contain max-h-[460px] opacity-85"
              />

              {/* Holographic False-Color Heatmap Simulation Overlay */}
              {showIsotherms && selectedPreset.deltaT > 2 && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-2/3 h-48 rounded-3xl bg-gradient-to-r from-blue-600/40 via-purple-600/60 to-red-600/70 blur-xl opacity-90 animate-pulse" />
                  
                  {/* Spot Temperature Probe Callout */}
                  <div className="absolute top-1/3 left-1/3 bg-slate-900/90 border border-amber-400 text-amber-300 font-mono text-xs p-2 rounded shadow-lg">
                    <div>SP01: {selectedPreset.peakTemp}°C</div>
                    <div className="text-[10px] text-slate-300">ΔT = +{selectedPreset.deltaT}°C</div>
                  </div>

                  <div className="absolute bottom-1/4 right-1/3 bg-slate-900/90 border border-blue-400 text-blue-300 font-mono text-xs p-2 rounded shadow-lg">
                    <div>REF01: {selectedPreset.ambientTemp}°C (Ambient)</div>
                  </div>
                </div>
              )}

              {/* Radiometric Palette Color Bar */}
              <div className="absolute right-4 top-1/2 -translate-y-1/2 flex flex-col items-center bg-slate-900/80 p-2 rounded border border-slate-700 font-mono text-[10px] text-white">
                <span>{selectedPreset.peakTemp + 5}°C</span>
                <div className="w-3 h-32 my-1 rounded bg-gradient-to-b from-white via-red-500 via-purple-600 to-blue-900" />
                <span>{selectedPreset.ambientTemp - 5}°C</span>
              </div>
            </div>

            {/* Bottom Spectrum Metadata */}
            <div className="p-3 bg-slate-900 border-t border-slate-800 flex items-center justify-between text-xs font-mono text-slate-400">
              <span>PALETTE: IRONBOW // EMISSIVITY: 0.92</span>
              <span>SOLAR LOAD NORMALIZED: YES</span>
            </div>
          </div>

          {/* Isotherm Threshold Slider */}
          <div className="card p-4 bg-white border border-slate-200 rounded-xl space-y-2">
            <div className="flex justify-between items-center text-xs font-semibold text-slate-800">
              <span>Isothermal Differential Threshold (ΔT)</span>
              <span className="font-mono text-amber-700 font-bold bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                +{isothermThreshold.toFixed(1)}°C
              </span>
            </div>
            <input
              type="range"
              min="1.0"
              max="15.0"
              step="0.5"
              value={isothermThreshold}
              onChange={(e) => setIsothermThreshold(parseFloat(e.target.value))}
              className="w-full accent-amber-600 cursor-pointer"
            />
            <button
              onClick={handleAnalyze}
              disabled={isLoading}
              className="btn btn-primary w-full py-2.5 text-xs font-semibold shadow-sm flex items-center justify-center gap-2 mt-2"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
              <span>{isLoading ? 'Recalibrating Ambient Normalization...' : 'Recalibrate Radiometric Differential'}</span>
            </button>
          </div>
        </div>

        {/* Right Column: Quantitative Telemetry */}
        <div className="lg:col-span-5 space-y-6">
          <div className="card p-5 bg-white border border-slate-200 rounded-xl shadow-sm space-y-5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <h3 className="font-bold text-slate-900 text-base">Moisture Ingress Risk</h3>
                <p className="text-xs text-slate-500">
                  Calibrated against ASTM C1060 thermographic guidelines
                </p>
              </div>
              <span
                className={`telemetry-pill font-bold ${
                  selectedPreset.severity === 'critical'
                    ? 'bg-red-50 text-red-700 border border-red-200'
                    : selectedPreset.severity === 'high'
                    ? 'bg-amber-50 text-amber-700 border border-amber-200'
                    : 'bg-green-50 text-green-700 border border-green-200'
                }`}
              >
                {selectedPreset.severity.toUpperCase()} RISK
              </span>
            </div>

            {/* Key Measurements */}
            <div className="grid grid-cols-2 gap-3 font-mono text-xs">
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                <div className="text-slate-500 text-[10px]">DELTA T (ΔT)</div>
                <div className="text-xl font-bold text-amber-600 mt-1">
                  +{selectedPreset.deltaT.toFixed(1)}°C
                </div>
                <div className="text-[10px] text-slate-400">vs Dry Sound Cladding</div>
              </div>

              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                <div className="text-slate-500 text-[10px]">MOISTURE PROBABILITY</div>
                <div className="text-xl font-bold text-blue-600 mt-1">
                  {(selectedPreset.moistureProb * 100).toFixed(0)}%
                </div>
                <div className="text-[10px] text-slate-400">Bayesian Ingress Model</div>
              </div>

              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                <div className="text-slate-500 text-[10px]">WETTED EXTENT</div>
                <div className="text-xl font-bold text-slate-900 mt-1">
                  {selectedPreset.wettedAreaPercent}%
                </div>
                <div className="text-[10px] text-slate-400">Of Inspection Span</div>
              </div>

              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                <div className="text-slate-500 text-[10px]">AMBIENT BASELINE</div>
                <div className="text-xl font-bold text-slate-900 mt-1">
                  {selectedPreset.ambientTemp}°C
                </div>
                <div className="text-[10px] text-slate-400">Weather Station Ref</div>
              </div>
            </div>

            {/* Diagnostic Description */}
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-2 text-xs">
              <div className="font-bold text-slate-900">Analysis Summary</div>
              <p className="text-slate-600 leading-relaxed font-sans">
                {selectedPreset.description}
              </p>
            </div>

            {/* Engineering Recommendation */}
            <div className="p-4 rounded-xl bg-slate-900 text-white space-y-3 font-mono text-xs">
              <div className="flex items-center gap-2 text-amber-400 font-bold">
                <AlertTriangle className="h-4 w-4" />
                <span>INTEGRITY RECOMMENDATION</span>
              </div>
              <p className="text-slate-300 font-sans text-xs leading-relaxed">
                {selectedPreset.recommendation}
              </p>
              <div className="pt-2 border-t border-slate-800 flex items-center justify-between">
                <Link to="/cmms" className="text-blue-400 hover:underline flex items-center gap-1">
                  <span>Push to SAP PM</span>
                  <ArrowRight className="h-3 w-3" />
                </Link>
                <Link to="/tools/ffs" className="text-blue-400 hover:underline">
                  Evaluate Wall Loss (FFS) →
                </Link>
              </div>
            </div>

          </div>
        </div>

      </div>
    </div>
  );
}
